import { useCallback } from 'react'
import { Box, Typography } from '@mui/material'
import EditOutlined from '@mui/icons-material/EditOutlined'
import AutoAwesomeOutlined from '@mui/icons-material/AutoAwesomeOutlined'
import { STEPS } from '../onboardingSteps'
import { useOnboardingV2 } from '../OnboardingContext'
import { useWizardData, readWizardData } from '../hooks/useWizardData'
import { useStep } from '../hooks/useStep'
import { stepHeadingSx, stepDescriptionSx } from '../styles'
import SelectableCard from '../components/SelectableCard'

const MODE_OPTIONS: {
  value: 'write' | 'guided'
  label: string
  description: string
  icon: React.ReactNode
}[] = [
  {
    value: 'write',
    label: 'Write it myself',
    description: 'Describe your funding need in your own words',
    icon: <EditOutlined />,
  },
  {
    value: 'guided',
    label: 'Help me find funding',
    description: "Answer a few questions and we'll help you create one",
    icon: <AutoAwesomeOutlined />,
  },
]

export default function FundingCreate() {
  const { goNext, goToStep } = useOnboardingV2()
  const [wizardData, setWizardData] = useWizardData()

  useStep({
    isValid: !!wizardData.fundingMode,
    beforeNext: async () => {
      const data = readWizardData()
      if (data.fundingMode === 'write') {
        goToStep(STEPS.FUNDING_DETAILS)
        return false
      }
    },
  })

  const handleSelect = useCallback(
    (value: string) => {
      const mode = value as 'write' | 'guided'
      if (mode === 'write') {
        // Clear sessionId so goBack reliably detects write path.
        // guidedFlowSnapshot is kept so guided recovery still works.
        setWizardData((prev) => {
          const draft = prev.writeModeDraft
          return {
            fundingMode: 'write' as const,
            writeModeDraft: draft
              ? { ...draft, fundingIdeasSessionId: undefined }
              : null,
          }
        })
        goToStep(STEPS.FUNDING_DETAILS)
      } else {
        setWizardData({ fundingMode: 'guided' })
        goNext()
      }
    },
    [setWizardData, goNext, goToStep],
  )

  return (
    <Box
      sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}
    >
      <Typography variant="h5" sx={{ ...stepHeadingSx, textAlign: 'center' }}>
        How would you like to create your funding request?
      </Typography>

      <Typography sx={{ ...stepDescriptionSx, textAlign: 'center' }}>
        Choose the option that works best for you.
      </Typography>

      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' },
          gap: 2,
          width: '100%',
        }}
      >
        {MODE_OPTIONS.map((option) => (
          <SelectableCard
            key={option.value}
            label={option.label}
            description={option.description}
            value={option.value}
            icon={option.icon}
            selected={wizardData.fundingMode === option.value}
            onClick={handleSelect}
            layout="vertical"
          />
        ))}
      </Box>
    </Box>
  )
}
